import { LONG_RUN_AFTER_FLOORS, MAX_STOPS, MILLI_PER_FLOOR, TICK_MS } from '../constants.js';
import type { Elevator, GameState } from '../types.js';
import { canBoard, performOneTransfer, queueableWith, transfersExhausted } from './transfer.js';

/**
 * The car's state machine.
 *
 * phase:     idle -> moving -> servicing -> idle
 * doorPhase: closed -> opening -> open -> closing -> closed
 *
 * `targetFloor` doubles as the pending destination while servicing: routing
 * elsewhere with the doors open records the target and asks the doors to close.
 */

/**
 * Open up at the current floor. Exported so `createInitialState` can start a
 * level with the doors already opening when people are waiting right there —
 * otherwise the player's very first action is tapping the floor they are on,
 * which reads as "nothing is happening" rather than as a decision.
 */
export function beginServicing(state: GameState): void {
  const { elevator, spec } = state;
  elevator.phase = 'servicing';
  elevator.doorPhase = 'opening';
  elevator.phaseTimerMs = spec.doorOpenMs;
  elevator.dwellMs = 0;
  elevator.closeRequested = false;
  elevator.boardQueue.length = 0;
  state.score.doorCycles += 1;
  // A squeeze used on the way counts at this stop (ADR-0055).
  if (elevator.squeeze === 1) elevator.squeeze = 2;
}

function beginClosing(state: GameState): void {
  const { elevator, spec } = state;
  elevator.doorPhase = 'closing';
  elevator.phaseTimerMs = spec.doorCloseMs;
  elevator.boardQueue.length = 0;
}

/**
 * How far the car moves this tick: the motor's speed, or the cable's once the
 * run is past its first floor (ADR-0033). The two are the same on a stock
 * rope, so a run without one moves exactly as it always did.
 */
function speedNow(state: GameState): number {
  const { elevator, speedMilliPerTick, longRunMilliPerTick } = state;
  const along = Math.abs(elevator.positionMilli - elevator.runFromMilli);
  return along >= LONG_RUN_AFTER_FLOORS * MILLI_PER_FLOOR ? longRunMilliPerTick : speedMilliPerTick;
}

function advanceMoving(state: GameState): void {
  const { elevator } = state;
  const speedMilliPerTick = speedNow(state);
  const target = elevator.targetFloor;
  if (target === null) {
    elevator.phase = 'idle';
    return;
  }

  const targetMilli = target * MILLI_PER_FLOOR;
  const delta = targetMilli - elevator.positionMilli;
  if (delta === 0) {
    beginServicing(state);
    return;
  }

  const stepMilli = Math.min(Math.abs(delta), speedMilliPerTick) * Math.sign(delta);
  elevator.positionMilli += stepMilli;
  state.score.distanceMilli += Math.abs(stepMilli);

  if (elevator.positionMilli === targetMilli) {
    beginServicing(state);
  }
}

function shouldClose(state: GameState, floor: number): boolean {
  const { elevator, spec } = state;
  if (elevator.dwellMs >= spec.maxDwellMs) return true;
  if (elevator.dwellMs < spec.minDwellMs) return false;
  if (elevator.closeRequested) return true;
  // In dispatch mode the player never taps "close", so an exhausted floor closes itself.
  // In select mode the player owns that call, up to maxDwell.
  return state.mode === 'dispatch' && transfersExhausted(state, floor);
}

function advanceServicing(state: GameState): void {
  const { elevator, spec } = state;
  const floor = elevator.positionMilli / MILLI_PER_FLOOR;

  switch (elevator.doorPhase) {
    case 'opening': {
      elevator.phaseTimerMs -= TICK_MS;
      if (elevator.phaseTimerMs <= 0) {
        elevator.doorPhase = 'open';
        elevator.phaseTimerMs = 0; // first transfer may happen at once
        elevator.dwellMs = 0;
      }
      return;
    }
    case 'open': {
      elevator.dwellMs += TICK_MS;
      if (elevator.phaseTimerMs > 0) {
        elevator.phaseTimerMs -= TICK_MS;
      } else if (performOneTransfer(state, floor)) {
        elevator.phaseTimerMs = spec.transferMs;
      }
      if (shouldClose(state, floor)) beginClosing(state);
      return;
    }
    case 'closing': {
      elevator.phaseTimerMs -= TICK_MS;
      if (elevator.phaseTimerMs <= 0) doorsShut(state, floor);
      return;
    }
    case 'closed': {
      // Unreachable while servicing; recover rather than wedge the run.
      elevator.phase = 'idle';
      return;
    }
  }
}

/** The doors are shut: on to the target, or the next stop, or park. */
function doorsShut(state: GameState, floor: number): void {
  const { elevator } = state;
  elevator.doorPhase = 'closed';
  elevator.closeRequested = false;
  // A squeeze lasts one stop: the doors are shut on it.
  if (elevator.squeeze === 2) elevator.squeeze = 0;
  // Done here: the next of the player's stops, if they tapped any ahead.
  // Someone here who could not get on through this opening (off at an
  // interchange, on again) gets the doors again first, as a parked car gives
  // them (`advanceIdle`).
  if (
    elevator.stops.length > 0 &&
    (elevator.targetFloor === null || elevator.targetFloor === floor)
  ) {
    if (someoneCanBoardAt(state, floor)) {
      beginServicing(state);
      return;
    }
    elevator.targetFloor = nextStop(state, floor);
  }
  const target = elevator.targetFloor;
  if (target !== null && target !== floor) {
    elevator.phase = 'moving';
    elevator.runFromMilli = elevator.positionMilli;
    state.score.trips += 1;
  } else {
    elevator.phase = 'idle';
    elevator.targetFloor = null;
  }
}

export function advanceElevator(state: GameState): void {
  switch (state.elevator.phase) {
    case 'moving':
      advanceMoving(state);
      return;
    case 'servicing':
      advanceServicing(state);
      return;
    case 'idle':
      advanceIdle(state);
      return;
  }
}

/**
 * A lift parked where somebody is standing opens its doors.
 *
 * ADR-0006 established this for tick zero: a level that starts on a busy floor
 * opens up, because otherwise the player's first action is tapping the floor
 * they are already on, which reads as "nothing is happening". The same argument
 * holds at every other moment and was never applied there.
 *
 * Without it, the doors shut the instant the current crowd is aboard and never
 * reopen for the person who arrives a second later. A playtest caught exactly
 * that on `24-the-nine-oclock`: the car sat at the lobby with two free seats and
 * closed doors while eleven people accumulated in front of it, for four and a
 * half seconds, until the player gave up and drove away. Three separate
 * complaints — "the other two didn't board", "nothing happens for four or five
 * seconds", "I feel like I'm doing something wrong" — were all this.
 */
function advanceIdle(state: GameState): void {
  const { elevator } = state;
  if (elevator.targetFloor !== null) return;

  const here = elevator.positionMilli / MILLI_PER_FLOOR;
  if (!Number.isInteger(here)) return;
  if (someoneCanBoardAt(state, here)) beginServicing(state);
}

function someoneCanBoardAt(state: GameState, floor: number): boolean {
  return state.passengers.some(
    (p) => p.state === 'waiting' && p.standingFloor === floor && canBoard(state, p, floor),
  );
}

/** Route the car. Valid in every phase; while servicing it also asks the doors to close. */
export function routeTo(state: GameState, floor: number): void {
  const { elevator } = state;
  if (!Number.isInteger(floor) || floor < 0 || floor >= state.floorCount) return;

  if (elevator.phase === 'servicing') {
    // Tapping the floor you are already parked on means "stay here", not "leave".
    // Treating it as a departure would cut boarding short the moment a player
    // pointed at the crowd they were trying to pick up.
    if (floor === elevator.positionMilli / MILLI_PER_FLOOR) {
      elevator.targetFloor = null;
      elevator.closeRequested = false;
      return;
    }
    elevator.targetFloor = floor;
    elevator.closeRequested = true;
    return;
  }

  if (elevator.phase === 'moving') {
    elevator.targetFloor = floor;
    return;
  }

  // idle
  const atFloor = elevator.positionMilli / MILLI_PER_FLOOR;
  if (floor === atFloor) {
    elevator.targetFloor = null;
    beginServicing(state);
  } else {
    elevator.targetFloor = floor;
    elevator.phase = 'moving';
    elevator.runFromMilli = elevator.positionMilli;
    state.score.trips += 1;
  }
}

/** The first of the player's stops that is not where the car stands, taken off the list. */
function nextStop(state: GameState, floor: number): number | null {
  const { stops } = state.elevator;
  while (stops.length > 0) {
    const next = stops.shift() as number;
    if (next !== floor) return next;
  }
  return null;
}

/** Where the car is still going: its target, unless that is the floor it stands at. */
function goingTo(state: GameState, car: Elevator = state.elevator): number | null {
  const target = car.targetFloor;
  if (target === null || car.phase === 'moving') return target;
  return target === car.positionMilli / MILLI_PER_FLOOR ? null : target;
}

/** The floors a car will still go to, in order: where it is going, then its stops (ADR-0052). */
export function planOf(state: GameState, car: Elevator = state.elevator): number[] {
  const going = goingTo(state, car);
  return going === null ? [...car.stops] : [going, ...car.stops];
}

/**
 * A stop in the player's order (a player asked for taps that queue, PLAYTEST_LOG
 * 2026-10-29). With nowhere to be, the car goes as `routeTo` sends it; with the
 * doors open it sets off once the floor is done, without cutting it short.
 * Going somewhere already, the floor joins the stops after it, up to
 * `MAX_STOPS` in all, and a floor already among them is taken off. `routeTo`
 * is untouched: every run recorded with it replays as it did.
 */
export function addStop(state: GameState, floor: number): void {
  const { elevator } = state;
  if (!Number.isInteger(floor) || floor < 0 || floor >= state.floorCount) return;

  const going = goingTo(state);
  if (going === null && elevator.stops.length === 0) {
    if (elevator.phase !== 'servicing') {
      routeTo(state, floor);
      return;
    }
    // At a floor with the doors working: the first stop, taken once it is done.
    if (floor !== elevator.positionMilli / MILLI_PER_FLOOR) elevator.stops.push(floor);
    return;
  }

  // At a stop with more to come, the stops are the whole route.
  if (floor === going) {
    dropCurrent(state);
    return;
  }
  const at = elevator.stops.indexOf(floor);
  if (at !== -1) {
    elevator.stops.splice(at, 1);
    return;
  }
  if ((going === null ? 0 : 1) + elevator.stops.length < MAX_STOPS) elevator.stops.push(floor);
}

/**
 * Forget the player's plan (the owner: "a button that cancels the queue is a
 * must", PLAYTEST_LOG 2026-10-29): every stop goes, and the car stops at the
 * first floor on its way, or stays where its doors are open.
 */
export function clearStops(state: GameState): void {
  state.elevator.stops.length = 0;
  if (goingTo(state) !== null) dropCurrent(state);
}

/** The floor the car is going to, taken off: on to the next stop, or stop soon. */
function dropCurrent(state: GameState): void {
  const { elevator } = state;
  const next = elevator.stops.shift();
  if (next !== undefined) {
    elevator.targetFloor = next;
    return;
  }
  if (elevator.phase !== 'moving') {
    elevator.targetFloor = null;
    elevator.closeRequested = false;
    return;
  }
  // The first floor it can stop at, the way it is going.
  const at = elevator.positionMilli / MILLI_PER_FLOOR;
  const up = (elevator.targetFloor ?? at) > at;
  elevator.targetFloor = up ? Math.ceil(at) : Math.floor(at);
}

/** Queue a tapped passenger for boarding. `select` mode only; a no-op otherwise. */
export function queueBoarding(state: GameState, passengerId: string): void {
  const { elevator } = state;
  if (state.mode !== 'select') return;
  if (elevator.phase !== 'servicing') return;
  if (elevator.doorPhase !== 'open' && elevator.doorPhase !== 'opening') return;
  if (elevator.boardQueue.includes(passengerId)) return;

  const floor = elevator.positionMilli / MILLI_PER_FLOOR;
  const passenger = state.passengers.find((p) => p.id === passengerId);
  if (passenger?.state !== 'waiting' || passenger.standingFloor !== floor) return;

  // Tapping one member of a group queues all of them. They board together by
  // rule, so making the player tap each one would be busywork that implies a
  // choice they do not actually have.
  for (const member of queueableWith(state, passenger)) {
    if (!elevator.boardQueue.includes(member.id)) elevator.boardQueue.push(member.id);
  }
}

/**
 * Let a waiting passenger on first (ADR-0025). `dispatch` mode only. The
 * latest marked goes to the front: when two people want the last seat, the
 * one the player chose most recently gets it. A group moves as one, since it
 * boards as one. Marking is a preference for the moment the doors open on
 * their floor; it never holds a door for someone who does not fit.
 */
export function markFirst(state: GameState, passengerId: string): void {
  if (state.mode !== 'dispatch') return;
  const passenger = state.passengers.find((p) => p.id === passengerId);
  if (passenger?.state !== 'waiting') return;
  const { priority } = state.elevator;
  const members = queueableWith(state, passenger).map((m) => m.id);
  for (const id of members) {
    const at = priority.indexOf(id);
    if (at !== -1) priority.splice(at, 1);
  }
  priority.unshift(...members);
}

export function requestClose(state: GameState): void {
  if (state.elevator.phase === 'servicing') state.elevator.closeRequested = true;
}
